import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  ColumnDef,
  flexRender,
  SortingState,
  PaginationState,
} from '@tanstack/react-table';
import { employeeService } from '../services/employeeService';
import { EmployeeRecord, EmployeeStatus, Position } from '../types';
import { FiSearch, FiFilter, FiEye, FiCheck, FiX, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight, FiDownload } from 'react-icons/fi';
import { LOCATIONS_OPTIONS } from '../types';
import { getPositions } from '../services/positionsService';
import toast from 'react-hot-toast';

const CandidatePoolPage: React.FC = () => {
  const [data, setData] = useState<EmployeeRecord[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [sorting, setSorting] = useState<SortingState>([]);

  // Temp filter states (for input fields)
  const [tempGlobalFilter, setTempGlobalFilter] = useState('');
  const [tempStatusFilter, setTempStatusFilter] = useState<string[]>(['candidate', 'under_review']);
  const [tempDepartmentFilter, setTempDepartmentFilter] = useState('');
  const [tempPositionFilter, setTempPositionFilter] = useState('');
  const [tempLocationFilter, setTempLocationFilter] = useState('');

  // Actual filter states (for API calls)
  const [globalFilter, setGlobalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>(['candidate', 'under_review']);
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');

  const [isExporting, setIsExporting] = useState(false);
  const [positions, setPositions] = useState<Position[]>([]);
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  const handleSearch = () => {
    // Reset to first page when searching
    setPagination({ pageIndex: 0, pageSize });
    // Apply the temp filters to actual filters
    setGlobalFilter(tempGlobalFilter);
    setStatusFilter(tempStatusFilter);
    setDepartmentFilter(tempDepartmentFilter);
    setPositionFilter(tempPositionFilter);
    setLocationFilter(tempLocationFilter);
  };

  const handleClearFilters = () => {
    setTempGlobalFilter('');
    setTempStatusFilter(['candidate', 'under_review']);
    setTempDepartmentFilter('');
    setTempPositionFilter('');
    setTempLocationFilter('');
    setGlobalFilter('');
    setStatusFilter(['candidate', 'under_review']);
    setDepartmentFilter('');
    setPositionFilter('');
    setLocationFilter('');
    setPagination({ pageIndex: 0, pageSize });
  };

  const exportToCSV = async () => {
    setIsExporting(true);
    try {
      const filters: Record<string, any> = { status: statusFilter };
      if (globalFilter) filters.searchTerm = globalFilter;
      if (departmentFilter) filters.department = departmentFilter;
      if (positionFilter) filters.position = positionFilter;
      if (locationFilter) filters.location = locationFilter;

      const response = await employeeService.getEmployees({
        page: 1,
        limit: 10000,
        filters: filters
      });

      const allData = response.data?.data || [];

      if (allData.length === 0) {
        toast.error('No data to export');
        return;
      }

      const csvHeaders = [
        'Full Names',
        'Also Known As',
        'Surname',
        'Maiden Name',
        'Date of Birth',
        'Gender',
        'Nationality',
        'ID Number',
        'Contact Number',
        'Email',
        'Tax Number',
        'Social Security Number',
        'Street Address',
        'Town/City',
        'Custom Town/City',
        'Bank',
        'Branch',
        'Account Number',
        'Account Type',
        'Account Name',
        'Marital Status',
        'Criminal Records',
        'Pre-existing Disability',
        'Disability Description',
        'Qualification',
        'Employment Status',
        'Current Employer',
        'Current Position',
        'Position Applied For',
        'Previously Employed by Trigon',
        'Previous Trigon Position',
        'Previous Trigon Employee Number',
        'Employee Number',
        'Job Grade',
        'Job Title',
        'Department',
        'Status',
        'Version',
        'Created At',
        'Updated At',
        'Created By',
        'Updated By',
        'Review Notes'
      ];

      const csvRows = allData.map((employee: EmployeeRecord) => [
        employee.fullNames || '',
        employee.alsoKnownAs || '',
        employee.surname || '',
        employee.maidenName || '',
        employee.dateOfBirth || '',
        employee.gender || '',
        employee.nationality || '',
        employee.idNumber || '',
        employee.contactNumber || '',
        employee.email || '',
        employee.taxNumber || '',
        employee.socialSecurityNumber || '',
        employee.residentialAddress?.streetAddress || '',
        employee.residentialAddress?.townCity || '',
        employee.residentialAddress?.customTownCity || '',
        employee.bankingDetails?.bank || '',
        employee.bankingDetails?.branch || '',
        employee.bankingDetails?.accountNumber || '',
        employee.bankingDetails?.accountType || '',
        employee.bankingDetails?.accountName || '',
        employee.maritalStatus || '',
        employee.criminalRecords || '',
        employee.preExistingDisability || '',
        employee.disabilityDescription || '',
        employee.qualification || '',
        employee.employmentStatus || '',
        employee.currentEmployer || '',
        employee.currentPosition || '',
        Array.isArray(employee.positionAppliedFor) ? employee.positionAppliedFor.join(', ') : employee.positionAppliedFor || '',
        employee.previouslyEmployedByTrigon || '',
        employee.previousTrigonPosition || '',
        employee.previousTrigonEmployeeNumber || '',
        employee.employeeNumber || '',
        employee.jobGrade || '',
        employee.jobTitle || '',
        employee.department || '',
        employee.status || '',
        employee.version || '',
        new Date(employee.createdAt).toLocaleDateString(),
        new Date(employee.updatedAt).toLocaleDateString(),
        employee.createdBy || '',
        employee.updatedBy || '',
        employee.reviewNotes || ''
      ]);

      const csvContent = [csvHeaders, ...csvRows]
        .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `candidate_pool_${timestamp}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success('Export completed successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const filters: Record<string, any> = {
        status: statusFilter,
      };
      if (globalFilter) {
        filters.searchTerm = globalFilter;
      }
      if (departmentFilter) {
        filters.department = departmentFilter;
      }
      if (positionFilter) {
        filters.position = positionFilter;
      }
      if (locationFilter) {
        filters.location = locationFilter;
      }

      // Backend now uses Query with GSI - gets ALL matching records efficiently
      const response = await employeeService.getEmployees({
        page: pageIndex + 1,
        limit: pageSize,
        filters: filters
      });

      setData(response.data?.data || []);
      setTotalRecords(response.data?.pagination?.total || 0);
    } catch (error) {
      console.error('Failed to fetch candidates:', error);
      toast.error('Failed to fetch candidates');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pageIndex, pageSize, sorting, globalFilter, statusFilter, departmentFilter, positionFilter, locationFilter]);

  useEffect(() => {
    const loadPositions = async () => {
      try {
        const positionsData = await getPositions();
        setPositions(positionsData);
      } catch (error) {
        console.error('Failed to load positions:', error);
      }
    };
    loadPositions();
  }, []);



  const columns = useMemo<ColumnDef<EmployeeRecord>[]>(
    () => [
      {
        accessorKey: 'fullNames',
        header: 'Name',
        cell: info => {
          const record = info.row.original;
          return `${record.fullNames} ${record.surname}`;
        },
      },
      {
        accessorKey: 'email',
        header: 'Email',
      },
      {
        accessorKey: 'positionAppliedFor',
        header: 'Applied Position',
        cell: info => {
          const value = info.getValue();
          return Array.isArray(value) ? value.join(', ') : value;
        },
      },
      {
        accessorKey: 'department',
        header: 'Department',
        cell: info => info.getValue() || 'Not Assigned',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: info => {
          const status = info.getValue() as string;
          return (
            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(status)}`}>
              {status.replace('_', ' ').toUpperCase()}
            </span>
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Applied Date',
        cell: info => new Date(info.getValue() as string).toLocaleDateString(),
      },
      {
        id: 'view',
        header: 'View',
        cell: ({ row }) => {
          const record = row.original;
          return (
            <div className="flex space-x-2">
              <Link
                to={`/review/${record.id}`}
                className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                title="View Details"
              >
                <FiEye />
              </Link>
            </div>
          );
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination,
      globalFilter,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount: Math.ceil(totalRecords / pageSize),
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Candidate Pool</h1>
          <p className="text-gray-600">Review and manage candidates for hire</p>
        </div>
        <div>
          <button
            onClick={exportToCSV}
            disabled={isExporting}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FiDownload className="mr-2" />
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={tempGlobalFilter}
                onChange={(e) => setTempGlobalFilter(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search candidates..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={tempStatusFilter.join(',')}
              onChange={(e) => setTempStatusFilter(e.target.value ? e.target.value.split(',') : [])}
              className="border border-gray-300 rounded-lg px-3 py-2 w-full"
            >
              <option value="candidate,under_review">Candidate Pool</option>
              <option value="candidate">Candidates Only</option>
              <option value="under_review">Under Review Only</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
            <input
              type="text"
              value={tempDepartmentFilter}
              onChange={(e) => setTempDepartmentFilter(e.target.value)}
              placeholder="Filter by department"
              className="border border-gray-300 rounded-lg px-3 py-2 w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Position Filter</label>
            <select
              value={tempPositionFilter}
              onChange={(e) => setTempPositionFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 w-full"
            >
              <option value="">All Positions</option>
              {positions.filter(pos => pos.isActive).map(position => (
                <option key={position.id} value={position.name}>{position.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Location Filter</label>
            <select
              value={tempLocationFilter}
              onChange={(e) => setTempLocationFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 w-full"
            >
              <option value="">All Locations</option>
              {LOCATIONS_OPTIONS.map(location => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>
          </div>

          <div className="flex items-end gap-2">
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Search
            </button>
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Results Table */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th key={header.id} className="px-6 py-3 border-b-2 border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {header.isPlaceholder ? null : (
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? 'cursor-pointer select-none'
                              : '',
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: ' 🔼',
                            desc: ' 🔽',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, r) => (
                  <tr key={r} className="border-b">
                    {columns.map((_col, c) => (
                      <td key={c} className="px-6 py-4 whitespace-nowrap">
                        <div className="relative overflow-hidden rounded bg-primary-300 h-4" aria-hidden="true">
                          <div
                            className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white to-transparent animate-shimmer motion-reduce:hidden"
                            style={{ animationDelay: `${r * 70 + c * 30}ms` }}
                          />
                        </div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : table.getRowModel().rows.length === 0 ? (
                <tr><td colSpan={columns.length} className="text-center py-8">No candidates found.</td></tr>
              ) : (
                table.getRowModel().rows.map(row => (
                  <tr key={row.id} className="border-b hover:bg-gray-50">
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-6">
          <span className="text-sm text-gray-700">
            Page{' '}
            <strong>
              {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
            </strong>{' '}
            | Total Records: {totalRecords}
          </span>
          <div className="flex items-center space-x-2">
            <button onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()} className="p-2 border rounded disabled:opacity-50 hover:bg-gray-50">
              <FiChevronsLeft />
            </button>
            <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()} className="p-2 border rounded disabled:opacity-50 hover:bg-gray-50">
              <FiChevronLeft />
            </button>
            <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()} className="p-2 border rounded disabled:opacity-50 hover:bg-gray-50">
              <FiChevronRight />
            </button>
            <button onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()} className="p-2 border rounded disabled:opacity-50 hover:bg-gray-50">
              <FiChevronsRight />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'candidate':
      return 'bg-yellow-100 text-yellow-800';
    case 'under_review':
      return 'bg-purple-100 text-purple-800';
    case 'requested_rework':
      return 'bg-orange-100 text-orange-800';
    case 'current_employee':
      return 'bg-green-100 text-green-800';
    case 'rejected':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export default CandidatePoolPage;
